import { QueryClient, QueryCache, MutationCache, onlineManager } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { get, set, del } from 'idb-keyval'

import { ApiError } from '@/shared/lib/api-client'
import { serverReachability } from '@/shared/lib/server-reachability'
import { getDirtyScores } from '@/features/scoring/api'
import type { JudgeStateWithDirty } from '@/features/scoring/api'

function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // On 401 from any non-auth query, clear auth cache so guards redirect
      if (query.queryKey[0] !== 'auth' && isUnauthorized(error)) {
        queryClient.setQueryData(['auth', 'me'], null)
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isUnauthorized(error)) {
        queryClient.setQueryData(['auth', 'me'], null)
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: (failureCount, error) => {
        if (isUnauthorized(error)) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isUnauthorized(error)) return false
        return failureCount < 2
      },
    },
  },
})

const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => get(key),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
})

export const [, restorePromise] = persistQueryClient({
  queryClient,
  persister,
  maxAge: 24 * 60 * 60 * 1000,
  buster: 'v2',
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // Always persist auth — judge must survive offline refresh
      if (query.queryKey[0] === 'auth') return true
      // Always persist judge-state — participant list needed offline
      if (query.queryKey[0] === 'competitions' && query.queryKey[2] === 'judge-state') return true
      // Always persist all my-score — prefetched data needed offline
      if (query.queryKey[0] === 'my-score') return true
      return query.state.status === 'success'
    },
  },
})

/** Check if any judge-state cache entry has pending offline activations. */
function hasPendingActivations(): boolean {
  const cache = queryClient.getQueryCache()
  for (const query of cache.getAll()) {
    if (query.queryKey[0] === 'competitions' && query.queryKey[2] === 'judge-state') {
      const data = query.state.data as JudgeStateWithDirty | undefined
      if (data?._dirtyActivations && data._dirtyActivations.length > 0) return true
    }
  }
  return false
}

function hasPendingOfflineData(): boolean {
  return getDirtyScores(queryClient).length > 0 || hasPendingActivations()
}

// After IndexedDB cache is restored, decide whether to invalidate.
// If offline or dirty data exists: preserve everything (sync engine handles reconciliation).
// If online and clean: refresh from server.
restorePromise.then(() => {
  const isOffline = serverReachability.getState() !== 'reachable'
  const hasDirty = hasPendingOfflineData()

  if (isOffline || hasDirty) {
    // Don't invalidate — preserve cached data for offline use.
    // The sync engine will handle reconciliation when we go online.
    return
  }

  // Online with clean data — refresh everything from server
  queryClient.invalidateQueries()
})

// Override TanStack Query's online detection with server reachability.
// Key change for V2: on 'reachable', we do NOT set online immediately.
// The sync engine sets online AFTER pushing dirty data.
// On 'unreachable', we set offline immediately to pause queries/mutations.
onlineManager.setEventListener((setOnline) => {
  const initialState = serverReachability.getState()
  const initialHasDirty = hasPendingOfflineData()

  // Start online only if reachable AND no dirty data to sync
  const initialOnline = initialState === 'reachable' && !initialHasDirty
  console.log('[GATE] initial state:', {
    reachable: initialState === 'reachable',
    hasDirty: initialHasDirty,
    online: initialOnline,
  })
  setOnline(initialOnline)

  return serverReachability.subscribe((state) => {
    if (state === 'unreachable') {
      console.log('[GATE] closing — server unreachable')
      setOnline(false)
    }
    // When 'reachable': do NOT setOnline(true) here.
    // The sync engine calls openGate() after sync completes.
  })
})

/** Called by the sync engine after dirty data is pushed to the server. */
export function openGate(): void {
  console.log('[GATE] opening — setting TQ online')
  onlineManager.setOnline(true)
}

export function clearPersistedCache(): void {
  queryClient.clear()
  del('REACT_QUERY_OFFLINE_CACHE').catch(() => {})
}
